import { create } from 'zustand';
import { useAuthStore } from './authStore';

export type DatePreset = 'currentMonth' | 'lastMonth' | 'currentYear' | 'jan25' | 'feb25' | 'mar25' | 'apr25' | 'may25' | 'jun25' | 'jul25' | 'aug25' | 'sep25' | 'oct25' | 'nov25' | 'dec25';

interface FilterState {
    initialDate: string;
    finalDate: string;
    activePreset: DatePreset | '';
    selectedCarteiraId?: string;
    setDates: (initial: string, final: string) => void;
    setPreset: (preset: DatePreset) => void;
    setSelectedCarteiraId: (id?: string) => void;
}

/**
 * LÓGICA DE CÁLCULO DE DATAS
 * Função centralizada que decide entre o cálculo fiscal ou de calendário.
 * @param preset A predefinição de data (ex: 'currentMonth').
 * @param isSupervisor Se o usuário logado é um supervisor.
 * @param selectedCarteiraId O ID da carteira selecionada (se houver).
 * @returns As datas de início e fim formatadas.
 */
const calculateDates = (preset: DatePreset, isSupervisor: boolean, selectedCarteiraId?: string) => {
    // CONDIÇÃO PRINCIPAL: Usa o mês calendário APENAS se o usuário for supervisor
    // E NENHUMA carteira específica estiver selecionada.
    const useCalendarMonth = isSupervisor && !selectedCarteiraId;

    const today = new Date();
    let startDate: Date;
    let endDate: Date;

    if (useCalendarMonth) {
        // --- LÓGICA DE MÊS CALENDÁRIO (Supervisor vendo a equipe toda) ---
        switch (preset) {
            case 'currentMonth':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'currentYear':
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = new Date(today.getFullYear(), 11, 31);
                break;
            // Períodos mensais específicos (calendário)
            case 'jan25':
                startDate = new Date(2025, 0, 1);
                endDate = new Date(2025, 0, 31);
                break;
            case 'feb25':
                startDate = new Date(2025, 1, 1);
                endDate = new Date(2025, 1, 28);
                break;
            case 'mar25':
                startDate = new Date(2025, 2, 1);
                endDate = new Date(2025, 2, 31);
                break;
            case 'apr25':
                startDate = new Date(2025, 3, 1);
                endDate = new Date(2025, 3, 30);
                break;
            case 'may25':
                startDate = new Date(2025, 4, 1);
                endDate = new Date(2025, 4, 31);
                break;
            case 'jun25':
                startDate = new Date(2025, 5, 1);
                endDate = new Date(2025, 5, 30);
                break;
            case 'jul25':
                startDate = new Date(2025, 6, 1);
                endDate = new Date(2025, 6, 31);
                break;
            case 'aug25':
                startDate = new Date(2025, 7, 1);
                endDate = new Date(2025, 7, 31);
                break;
            case 'sep25':
                startDate = new Date(2025, 8, 1);
                endDate = new Date(2025, 8, 30);
                break;
            case 'oct25':
                startDate = new Date(2025, 9, 1);
                endDate = new Date(2025, 9, 31);
                break;
            case 'nov25':
                startDate = new Date(2025, 10, 1);
                endDate = new Date(2025, 10, 30);
                break;
            case 'dec25':
                startDate = new Date(2025, 11, 1);
                endDate = new Date(2025, 11, 31);
                break;
        }
    } else {
        // --- LÓGICA DE MÊS FISCAL (Vendedor ou Supervisor vendo um vendedor) ---
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const currentDay = today.getDate();

        switch (preset) {
            case 'currentMonth':
                if (currentDay >= 26) {
                    startDate = new Date(currentYear, currentMonth, 26);
                    endDate = new Date(currentYear, currentMonth + 1, 25);
                } else {
                    startDate = new Date(currentYear, currentMonth - 1, 26);
                    endDate = new Date(currentYear, currentMonth, 25);
                }
                break;
            case 'lastMonth':
                if (currentDay >= 26) {
                    startDate = new Date(currentYear, currentMonth - 1, 26);
                    endDate = new Date(currentYear, currentMonth, 25);
                } else {
                    startDate = new Date(currentYear, currentMonth - 2, 26);
                    endDate = new Date(currentYear, currentMonth - 1, 25);
                }
                break;
            case 'currentYear':
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = new Date(today.getFullYear(), 11, 31);
                break;
            // Períodos mensais específicos (fiscal - dia 26 ao dia 25)
            case 'jan25':
                startDate = new Date(2024, 11, 26); // 26 de dezembro de 2024
                endDate = new Date(2025, 0, 25);    // 25 de janeiro de 2025
                break;
            case 'feb25':
                startDate = new Date(2025, 0, 26);  // 26 de janeiro de 2025
                endDate = new Date(2025, 1, 25);    // 25 de fevereiro de 2025
                break;
            case 'mar25':
                startDate = new Date(2025, 1, 26);  // 26 de fevereiro de 2025
                endDate = new Date(2025, 2, 25);    // 25 de março de 2025
                break;
            case 'apr25':
                startDate = new Date(2025, 2, 26);  // 26 de março de 2025
                endDate = new Date(2025, 3, 25);    // 25 de abril de 2025
                break;
            case 'may25':
                startDate = new Date(2025, 3, 26);  // 26 de abril de 2025
                endDate = new Date(2025, 4, 25);    // 25 de maio de 2025
                break;
            case 'jun25':
                startDate = new Date(2025, 4, 26);  // 26 de maio de 2025
                endDate = new Date(2025, 5, 25);    // 25 de junho de 2025
                break;
            case 'jul25':
                startDate = new Date(2025, 5, 26);  // 26 de junho de 2025
                endDate = new Date(2025, 6, 25);    // 25 de julho de 2025
                break;
            case 'aug25':
                startDate = new Date(2025, 6, 26);  // 26 de julho de 2025
                endDate = new Date(2025, 7, 25);    // 25 de agosto de 2025
                break;
            case 'sep25':
                startDate = new Date(2025, 7, 26);  // 26 de agosto de 2025
                endDate = new Date(2025, 8, 25);    // 25 de setembro de 2025
                break;
            case 'oct25':
                startDate = new Date(2025, 8, 26);  // 26 de setembro de 2025
                endDate = new Date(2025, 9, 25);    // 25 de outubro de 2025
                break;
            case 'nov25':
                startDate = new Date(2025, 9, 26);  // 26 de outubro de 2025
                endDate = new Date(2025, 10, 25);   // 25 de novembro de 2025
                break;
            case 'dec25':
                startDate = new Date(2025, 10, 26); // 26 de novembro de 2025
                endDate = new Date(2025, 11, 25);   // 25 de dezembro de 2025
                break;
        }
    }

    const formatDate = (date: Date) => date.toISOString().split('T')[0];
    return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
};

export const useFilterStore = create<FilterState>((set, get) => {
    const role = useAuthStore.getState().role;
    const isSupervisor = role === 'supervisor' || role === 'diretor' || role === 'gerente' || role === 'admin';
    const { startDate, endDate } = calculateDates('currentMonth', isSupervisor, undefined);

    return {
        initialDate: startDate,
        finalDate: endDate,
        activePreset: 'currentMonth',
        selectedCarteiraId: undefined,

        setDates: (initial, final) => set({
            initialDate: initial,
            finalDate: final,
            activePreset: '',
        }),

        setPreset: (preset) => {
            const role = useAuthStore.getState().role;
            const isSupervisor = role === 'supervisor' || role === 'diretor' || role === 'gerente' || role === 'admin';
            const { selectedCarteiraId } = get();
            const { startDate, endDate } = calculateDates(preset, isSupervisor, selectedCarteiraId);
            set({
                activePreset: preset,
                initialDate: startDate,
                finalDate: endDate,
            });
        },

        setSelectedCarteiraId: (id) => {
            set({ selectedCarteiraId: id });
            // RECALCULA AS DATAS: Ao mudar a carteira, a lógica de data pode mudar.
            // Por isso, chamamos o setPreset novamente para forçar a atualização.
            const { activePreset } = get();
            if (activePreset) {
                get().setPreset(activePreset);
            }
        },
    };
});